'use client';
import { ApiErrorCode } from "@prisma/client";
import { prepareObjectWithDates } from "./prepare-object-with-dates";
import { ApiErrorData } from "@/constants/server-errors";

/**
 * Makes a POST HTTP request to the API with proper error handling and typing
 *
 * @template TInput The type of the request body
 * @template TResponse The type of the expected response
 * @param url The URL to make the request to
 * @param body The body data to send
 * @returns A promise that resolves to the response data or rejects with an error
 */
export async function apiPost<
    TResponse extends { data: any },
    TInput extends Record<string, unknown> | FormData,
    TErrorCode extends ApiErrorCode = ApiErrorCode,
    TError extends { error: TErrorCode, data: ApiErrorData<TErrorCode, TResponse['data']> } = { error: TErrorCode, data: ApiErrorData<TErrorCode, TResponse['data']> }
>(url: string, body: TInput) {
    const apiUrl = "/api/" + url

    // Check if body is FormData
    const isFormData = body instanceof FormData;

    return new Promise<TResponse['data']>(async (resolve, reject) => {
        try {
            const fetchOptions: RequestInit = {
                method: 'POST',
                credentials: 'include', // This includes cookies in the request (sessionToken)
            };

            if (isFormData) {
                // For FormData, don't set Content-Type header - browser will set it with boundary
                fetchOptions.body = body as FormData;
            } else {
                // For JSON, set Content-Type and stringify the body
                const preparedBody = prepareObjectWithDates(body as Record<string, unknown>);
                fetchOptions.headers = {
                    'Content-Type': 'application/json',
                };
                fetchOptions.body = JSON.stringify(preparedBody);
            }

            const response = await fetch(apiUrl, fetchOptions);

            // Parse the response as JSON
            const result = (await response.json()) as TError | TResponse;

            // Reject on API error payload (envelope includes top-level `error` from create-apiResponse).
            // Also: Prisma `ApiErrorCode` must be coerced to string in some runtimes, so do not use `typeof === "string"` only.
            if (
                typeof result === "object" &&
                result !== null &&
                "error" in result &&
                (result as { error: unknown }).error != null &&
                (result as { error: unknown }).error !== undefined
            ) {
                reject(result as TError);
                return;
            }

            if (!response.ok) {
                reject(
                    (typeof result === "object" && result !== null && "error" in result
                        ? (result as TError)
                        : { error: ApiErrorCode.INTERNAL_APP_ERROR }) as TError,
                );
                return;
            }

            // Return the data on success
            resolve((result as TResponse).data);
        } catch (result: unknown) {
            if (typeof result === "object" && result !== null && "error" in result && (result as { error: unknown }).error != null) {
                reject(result as TError);
            }
            else {
                reject({ error: ApiErrorCode.INTERNAL_APP_ERROR } as TError);
            }
        }
    })
}